import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

interface IDateRangeReturnType {
  minDate: Date;
  maxDate: Date;
}

export function formatDayJsDate(date: Dayjs | string, format = 'YYYY-MM-DD') {
  dayjs.extend(advancedFormat);
  let ans;
  if (!date) {
    return null;
  }
  try {
    if (typeof date === 'string') {
      if (format === 'verbose') {
        ans = dayjs(date).format('Do MMM, YYYY, hh:mm a');
      }
      ans = dayjs(date).format(format);
    } else if (format === 'verbose') {
      ans = dayjs(date).format('Do MMM, YYYY, hh:mm a');
    } else {
      ans = date.format(format);
    }
    return ans;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export function convertDateToDayJs(date: Date | string): Dayjs {
  try {
    return dayjs(date);
  } catch (err) {
    console.error(err);
    return null;
  }
}

export function isValidDate(dateString: string): boolean {
  try {
    if (typeof dateString !== 'string') {
      return false;
    }
    // Regular expression for ISO 8601 date format (basic validation)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3}Z)?)?$/;

    // Regular expression for date format like 'Wed Dec 11 2024 00:00:00 GMT+0530 (India Standard Time)'
    const dateObjectRegex =
      /^[A-Za-z]{3}\s[A-Za-z]{3}\s\d{2}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT[+-]\d{4}\s\([A-Za-z\s]+\)$/;

    // Check if the dateString matches either the ISO or the date object format regex
    if (!isoDateRegex.test(dateString) && !dateObjectRegex.test(dateString)) {
      return false;
    }
    // Check if the date string can be parsed into a valid Date object
    const date = new Date(dateString);

    // Check if the date is valid (it shouldn't be "Invalid Date")
    // The date object's getTime() method will return NaN if the date is invalid
    return !isNaN(date.getTime());
  } catch (err) {
    console.error(err);
    return false;
  }
}

export function getFinancialYearDateRange(
  today = new Date(),
): IDateRangeReturnType {
  const year = today.getFullYear();
  const month = today.getMonth();

  let minDate;
  const maxDate = today;

  if (month >= 0 && month <= 2) {
    // Jan, Feb, Mar (months 0, 1, 2)
    minDate = new Date(year - 1, 3, 1); // 1 April last year
  } else {
    // April to Dec (months 3-11)
    minDate = new Date(year, 3, 1); // 1 April this year
  }

  return { minDate, maxDate };
}

// New function to allow future dates till end of financial year
export function getFinancialYearDateRangeWithFuture(
  today = new Date(),
): IDateRangeReturnType {
  const year = today.getFullYear();
  const month = today.getMonth(); // JS months: 0-11

  let minDate;
  let maxDate;

  if (month >= 0 && month <= 2) {
    // Jan, Feb, Mar (months 0, 1, 2)
    minDate = new Date(year - 1, 3, 1); // 1 April last year
    maxDate = new Date(year, 2, 31); // 31 March this year
  } else {
    // April to Dec (months 3-11)6
    minDate = new Date(year, 3, 1); // 1 April this year
    maxDate = new Date(year + 1, 2, 31); // 31 March next year
  }

  return { minDate, maxDate };
}

export function getFinancialYearDateRangeForTour(
  today = new Date(),
): IDateRangeReturnType {
  const year = today.getFullYear();
  const month = today.getMonth();
  const startYear = month >= 3 ? year : year - 1;

  const minDate = new Date(startYear, 3, 1);
  const maxDate = new Date(startYear + 1, 3, 30);

  return { minDate, maxDate };
}

/**
 * Formats claim month and year in "Month, Year" format
 * @param claimMonth - Month number (1-12) or null/undefined
 * @param claimYear - Year number or null/undefined
 * @returns Formatted string like "April, 2025" or "-" if month is missing
 */
export function formatClaimMonthYear(
  claimMonth?: number | null,
  claimYear?: number | null,
): string {
  // If no month, return "-"
  if (!claimMonth || claimMonth < 1 || claimMonth > 12) {
    return '-';
  }

  // Get current year if claimYear is not provided
  const year = claimYear || new Date().getFullYear();

  // Month names array (1-based index)
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Get month name (claimMonth is 1-based, so subtract 1 for array index)
  const monthName = monthNames[claimMonth - 1];

  return `${monthName}, ${year}`;
}

/**
 * Returns true when the input date is before today's date (day precision).
 * Useful for disabling past dates in date pickers.
 */
export function isDateBeforeToday(
  date: Dayjs | Date | string | null | undefined,
): boolean {
  if (!date) return false;
  return dayjs(date).startOf('day').isBefore(dayjs().startOf('day'));
}

/**
 * Formats date as "DD MMM YYYY" (e.g. 03 Apr 2026) — no time.
 */
export function formatDateOnly(date: Dayjs | string | null | undefined): string {
  if (!date) return '-';
  const d = dayjs(date);
  return d.isValid() ? d.format('DD MMM YYYY') : '-';
}


export function getCustomFinancialDateRange(today = new Date()): IDateRangeReturnType {
  const year = today.getFullYear();

  // Min date = 1 January of current year
  const minDate = new Date(year, 0, 1); // months are 0-indexed, so 0 = January

  // Max date = 30 April of next year
  const maxDate = new Date(year + 1, 3, 30); // 3 = April, 30th day

  return { minDate, maxDate };
}

export function getCustomFinancialConveyanceDateRange(today = new Date()): IDateRangeReturnType {
  const year = today.getFullYear();

  // Min date = 1 Jan of last year
  const minDate = new Date(year, 0, 1); // months are 0-indexed, so 0 = Jan

  // Max date = 30 April of next year
  const maxDate = new Date(year + 1, 3, 30); // 3 = April, 30th day

  return { minDate, maxDate };
}
